import { useMemo } from "react";
import type { ProtoPlayerBio } from "@catbento/shared";
import { readSnapshot } from "../data/snapshot.js";
import { getPlayerBio, firstPlayerName, playerNameOf, type PlayerId } from "../data/players.js";
import { KNOWN_LEAGUE_NAMES } from "../live/known-leagues.js";
import { LIVE } from "../live/flag.js";
import { liveApi } from "../live/api.js";
import { useLiveQuery } from "../live/query.js";
import { loadPlayerBio } from "../live/bio.js";

// Phase 02 seam: one player's Bio. Two hooks, so going live is per WIDGET
// (track2-plan.md §1): the Bio widget's own `useBioPlayer` (by id; live with
// ?live=1, T2-3), and the snapshot `usePlayerBio` (by name) that Squad still uses for
// its sticky-player default until the Squad slice.

/** A snapshot player's Bio by name, or null if the snapshot doesn't have her. */
export function usePlayerBio(name: string): ProtoPlayerBio | null {
  return useMemo(() => getPlayerBio(readSnapshot().players, name), [name]);
}

function useSnapshotBioPlayer(playerId: PlayerId | null): ProtoPlayerBio | null {
  return useMemo(() => {
    const players = readSnapshot().players;
    // The prototype's fall-back: no player → the first player (renderers-bio.js:30) — the
    // snapshot-order first key, not the sorted index (see firstPlayerName). The snapshot
    // site only ever asks for its own players (bridge.tsx throws on any other id).
    const name = playerNameOf(players, playerId) ?? firstPlayerName(players);
    return name === null ? null : getPlayerBio(players, name);
  }, [playerId]);
}

/** Live: any player in the DB. Never the first-player fall-back — a module with no player
 *  shows the widget's own "No player data.", and an unknown id is an error (Retry). */
function useLiveBioPlayer(playerId: PlayerId | null): ProtoPlayerBio | null {
  const key = playerId === null ? null : `bio:${playerId}`;
  return useLiveQuery(key, () => loadPlayerBio(liveApi(), playerId as PlayerId, KNOWN_LEAGUE_NAMES));
}

/** The Bio widget's player, by id. Live with ?live=1. */
export const useBioPlayer: (playerId: PlayerId | null) => ProtoPlayerBio | null = LIVE
  ? useLiveBioPlayer
  : useSnapshotBioPlayer;
